import React, { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import Button from "@mui/material/Button";
import AddTagDrawer from "./AddTagDrawer";
import {
  KINDS,
  KIND_LABEL,
  KIND_PLURAL,
  loadTaxonomy,
  getProductTaxonomy,
  getSkuTaxonomy,
  toOption,
  chipColor,
} from "./taxonomyApi";

/**
 * Attach Industry / Compliance / Certification to one product.
 *
 * Used in two places with the same contract: the Website Apps edit modal
 * (target "product", id = all_products.id) and the marketplace SKU detail
 * (target "marketplace", id = marketPlaces.id).
 *
 * The component does NOT save — it reports the current selection upward via
 * onChange and lets the host form submit it, so attaching tags is part of the
 * one Save the admin already presses rather than a second hidden write.
 *
 * react-select with isMulti is used because EditItem already imports
 * react-select for its category dropdowns; adding MUI Autocomplete here would
 * put two different multi-select idioms on the same screen.
 *
 * Props:
 *   target     "product" | "marketplace"
 *   id         the product/SKU id; null while creating (picker renders disabled)
 *   value      { industry: [ids], compliance: [ids], certification: [ids] } | null
 *   onChange   (selection) => void
 *   compact    tighter spacing for use inside a dense modal
 */
export default function TaxonomyPicker({ target, id, value, onChange, compact = false }) {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [drawerKind, setDrawerKind] = useState(null);

  const selection = value || { industry: [], compliance: [], certification: [] };

  const refreshCatalog = useCallback(async (signal) => {
    const data = await loadTaxonomy(signal);
    setCatalog(data);
    return data;
  }, []);

  // Load the vocabularies, and what this product already carries.
  useEffect(() => {
    const ac = new AbortController();
    let alive = true;

    (async () => {
      setLoading(true);
      setError("");
      try {
        await refreshCatalog(ac.signal);

        // Only fetch existing links when the host did not already supply them
        // and we have an id to fetch for.
        if (!value && id) {
          const attached =
            target === "product"
              ? await getProductTaxonomy(id, ac.signal)
              : await getSkuTaxonomy(id, ac.signal);
          if (alive) onChange?.(attached);
        }
      } catch (err) {
        if (alive && err?.name !== "AbortError") {
          setError(err?.message || "Could not load tags.");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      ac.abort();
    };
    // Intentionally keyed on identity only: re-running on every onChange would
    // refetch the selection the admin is actively editing and clobber it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, id]);

  const setKind = (kind, ids) => onChange?.({ ...selection, [kind]: ids });

  /**
   * Tint each option and selected chip with its own colour, so the admin sees
   * the same chip the public site will render.
   */
  const styles = {
    control: (base) => ({ ...base, minHeight: 38, borderColor: "#d0d5dd" }),
    // The edit modal clips its children, so the menu renders in a portal on
    // document.body. That portal WRAPPER carries its own stacking context and
    // defaults to z-index 1 — styling `menu` alone is not enough, the menu
    // then opens behind the modal backdrop (z-index 1000 in
    // Website_Apps/EditItem.module.css) and the field looks dead: clicking
    // shows nothing and typing filters a list you cannot see.
    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
    menu: (base) => ({ ...base, zIndex: 10000 }),
    multiValue: (base, state) => ({
      ...base,
      backgroundColor: `${state.data.color}1f`,
      borderRadius: 6,
    }),
    multiValueLabel: (base, state) => ({
      ...base,
      color: state.data.color,
      fontWeight: 600,
    }),
    multiValueRemove: (base, state) => ({
      ...base,
      color: state.data.color,
      ":hover": { backgroundColor: `${state.data.color}33`, color: state.data.color },
    }),
    option: (base, state) => ({
      ...base,
      display: "flex",
      alignItems: "center",
      gap: 8,
      backgroundColor: state.isFocused ? `${state.data.color}14` : "white",
      color: "#111",
      ":before": {
        content: '""',
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: 3,
        backgroundColor: state.data.color,
      },
    }),
  };

  if (error) {
    return (
      <div style={{ color: "#b42318", fontSize: 13, padding: "8px 0" }}>
        {error}{" "}
        <button
          type="button"
          onClick={() => refreshCatalog().catch((e) => setError(e.message))}
          style={{ background: "none", border: "none", color: "#2563eb", cursor: "pointer" }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: compact ? 10 : 14 }}>
      {KINDS.map((kind) => {
        const rows = catalog?.[kind] || [];
        const options = rows.map(toOption);
        const selected = options.filter((o) => (selection[kind] || []).includes(o.value));

        return (
          <div key={kind}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 4,
              }}
            >
              <label style={{ fontSize: 13, fontWeight: 600, color: "#344054" }}>
                {KIND_PLURAL[kind]}
              </label>
              <Button
                size="small"
                onClick={() => setDrawerKind(kind)}
                sx={{ textTransform: "none", minWidth: 0, px: 1, fontSize: 12 }}
              >
                + Add {KIND_LABEL[kind].toLowerCase()}
              </Button>
            </div>

            <Select
              isMulti
              isLoading={loading}
              isDisabled={!id}
              options={options}
              value={selected}
              onChange={(picked) => setKind(kind, (picked || []).map((p) => p.value))}
              styles={styles}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              placeholder={
                rows.length
                  ? `Select ${KIND_PLURAL[kind].toLowerCase()}…`
                  : `No ${KIND_PLURAL[kind].toLowerCase()} yet — add one`
              }
              noOptionsMessage={() => `No ${KIND_PLURAL[kind].toLowerCase()} yet`}
            />
          </div>
        );
      })}

      {!id && (
        <div style={{ fontSize: 12, color: "#667085" }}>
          Save the product first, then reopen to attach tags.
        </div>
      )}

      <AddTagDrawer
        open={Boolean(drawerKind)}
        kind={drawerKind}
        onClose={() => setDrawerKind(null)}
        onSaved={async (result) => {
          // Pull the refreshed vocabulary, then auto-select what was just
          // created — the admin opened the drawer from this product, so they
          // almost certainly want these attached to it.
          try {
            const fresh = await refreshCatalog();
            const createdIds = (result?.rows || []).map((r) => r.id);
            const existing = selection[drawerKind] || [];
            const merged = [...new Set([...existing, ...createdIds])];
            // Guard against a stale kind if the drawer closed mid-flight.
            if (fresh?.[drawerKind]) setKind(drawerKind, merged);
          } catch {
            /* the tags exist; the list will catch up on next open */
          }
        }}
      />
    </div>
  );
}

/** Read-only chip row, for showing what a product carries outside the editor. */
export function TaxonomyChips({ catalog, selection, limit = 0 }) {
  if (!catalog || !selection) return null;

  const all = [];
  for (const kind of KINDS) {
    const rows = catalog[kind] || [];
    for (const id of selection[kind] || []) {
      const row = rows.find((r) => r.id === id);
      if (row) all.push(row);
    }
  }
  if (!all.length) return null;

  const shown = limit > 0 ? all.slice(0, limit) : all;
  const hidden = all.length - shown.length;

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {shown.map((row) => (
        <span
          key={`${row.slug}-${row.id}`}
          title={row.name}
          style={{
            fontSize: 11,
            fontWeight: 600,
            padding: "2px 8px",
            borderRadius: 6,
            color: chipColor(row),
            background: `${chipColor(row)}1f`,
          }}
        >
          {row.name}
        </span>
      ))}
      {hidden > 0 && (
        <span style={{ fontSize: 11, color: "#667085", padding: "2px 6px" }}>+{hidden}</span>
      )}
    </div>
  );
}
